import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  SortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Checkbox,
  Group,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { IconGripVertical, IconZoomIn } from "@tabler/icons-react";
import dayjs from "dayjs";
import "dayjs/locale/vi";
import React, { CSSProperties, useState } from "react";
import { useHomeBlockStore } from "../../store/useHomeBlockStore";
import { ACCENT, designImage, HomeBlock } from "./homeBlockData";
import classes from "./homeBlock.module.css";

type ZoomableImageProps = {
  imageId: string;
  alt: string;
  children?: React.ReactNode;
};

// Ảnh thiết kế: bấm để mở bản lớn trong cửa sổ, Esc hoặc bấm ra ngoài để đóng
export const ZoomableImage = ({ imageId, alt, children }: ZoomableImageProps) => {
  const [opened, setOpened] = useState(false);
  return (
    <>
      <button
        type="button"
        className={classes.zoomButton}
        aria-label={`Phóng to ${alt}`}
        title="Bấm để phóng to"
        onClick={() => setOpened(true)}
      >
        {children ?? (
          <img
            src={designImage(imageId, 720)}
            alt={alt}
            referrerPolicy="no-referrer"
            className={classes.zoomThumb}
          />
        )}
        <span className={classes.zoomHint} aria-hidden="true">
          <IconZoomIn size={16} />
        </span>
      </button>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={alt}
        size="90%"
        centered
      >
        <img
          src={designImage(imageId, 2000)}
          alt={alt}
          referrerPolicy="no-referrer"
          style={{ display: "block", width: "100%", height: "auto" }}
        />
      </Modal>
    </>
  );
};

export const formatSchedule = (from: Date | null, to: Date | null) => {
  if (!from && !to) return "";
  const left = from ? dayjs(from).format("DD/MM") : "...";
  const right = to ? dayjs(to).format("DD/MM") : "...";
  return `${left} - ${right}`;
};

type CropImageProps = {
  src: string;
  alt: string;
  x: number;
  y: number;
  w: number;
  h: number;
  sourceWidth?: number;
  width?: number | string;
  radius?: number;
};

// Cắt một vùng của ảnh chụp thiết kế (toạ độ theo ảnh rộng sourceWidth px)
export const CropImage = ({
  src,
  alt,
  x,
  y,
  w,
  h,
  sourceWidth = 1280,
  width = "100%",
  radius,
}: CropImageProps) => (
  <div
    className={classes.crop}
    style={{ aspectRatio: `${w} / ${h}`, width, borderRadius: radius }}
  >
    <img
      src={src}
      alt={alt}
      referrerPolicy="no-referrer"
      style={{
        width: `${(sourceWidth / w) * 100}%`,
        left: `${(-x / w) * 100}%`,
        top: `${(-y / h) * 100}%`,
      }}
    />
  </div>
);

type SectionCardProps = {
  id?: string;
  title: string;
  right?: React.ReactNode;
  children: React.ReactNode;
  bodyPadding?: string | number;
  bodyGap?: string | number;
};

export const SectionCard = ({
  id,
  title,
  right,
  children,
  bodyPadding = 16,
  bodyGap = 14,
}: SectionCardProps) => (
  <Paper id={id} withBorder radius="md" component="section">
    <Group
      justify="space-between"
      gap="xs"
      px={16}
      py={14}
      style={{ borderBottom: "1px solid var(--hb-border)" }}
    >
      <Text component="h2" fw={700} fz={15} m={0}>
        {title}
      </Text>
      {right}
    </Group>
    <Stack p={bodyPadding} gap={bodyGap}>
      {children}
    </Stack>
  </Paper>
);

type SortableAreaProps = {
  ids: UniqueIdentifier[];
  onReorder: (fromIndex: number, toIndex: number) => void;
  strategy?: SortingStrategy;
  children: React.ReactNode;
};

export const SortableArea = ({
  ids,
  onReorder,
  strategy = verticalListSortingStrategy,
  children,
}: SortableAreaProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(active.id);
    const to = ids.indexOf(over.id);
    if (from < 0 || to < 0) return;
    onReorder(from, to);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={strategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
};

export const useSortableItem = (id: UniqueIdentifier) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: CSSProperties = {
    transform: transform
      ? `translate3d(${Math.round(transform.x)}px, ${Math.round(
          transform.y
        )}px, 0)`
      : undefined,
    transition,
  };

  return {
    setNodeRef,
    style,
    isDragging,
    handleProps: {
      ref: setActivatorNodeRef,
      ...attributes,
      ...listeners,
    },
  };
};

type DragHandleProps = {
  label: string;
  handleProps: ReturnType<typeof useSortableItem>["handleProps"];
  size?: number;
};

export const DragHandle = ({ label, handleProps, size = 18 }: DragHandleProps) => (
  <button
    type="button"
    className={classes.grip}
    aria-label={label}
    {...handleProps}
  >
    <IconGripVertical size={size} stroke={1.8} />
  </button>
);

export const reorderList = <T,>(list: T[], from: number, to: number) => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

type DisplaySettingsProps = {
  block: HomeBlock;
};

// Tab "Hiển thị và lịch": trường chung cho mọi mẫu block
export const DisplaySettings = ({ block }: DisplaySettingsProps) => {
  const updateBlock = useHomeBlockStore((s) => s.updateBlock);

  return (
    <SectionCard title="Hiển thị và lịch">
      <Switch
        color={ACCENT}
        labelPosition="left"
        label="Bật block trên trang chủ"
        checked={block.visible}
        onChange={(e) =>
          updateBlock(block.id, { visible: e.currentTarget.checked })
        }
        styles={{
          body: { justifyContent: "space-between" },
          label: { fontWeight: 600 },
        }}
      />
      <Checkbox.Group
        label="Hiển thị trên"
        value={[
          ...(block.desktop ? ["desktop"] : []),
          ...(block.mobile ? ["mobile"] : []),
        ]}
        onChange={(value) =>
          updateBlock(block.id, {
            desktop: value.includes("desktop"),
            mobile: value.includes("mobile"),
          })
        }
      >
        <Group mt={6} gap="lg">
          <Checkbox color={ACCENT} value="desktop" label="Máy tính" />
          <Checkbox color={ACCENT} value="mobile" label="Điện thoại" />
        </Group>
      </Checkbox.Group>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <DateTimePicker
          label="Hiện từ"
          placeholder="dd/mm/yyyy hh:mm"
          valueFormat="DD/MM/YYYY HH:mm"
          locale="vi"
          clearable
          value={block.from}
          onChange={(value) => updateBlock(block.id, { from: value })}
        />
        <DateTimePicker
          label="Đến"
          placeholder="Không giới hạn"
          valueFormat="DD/MM/YYYY HH:mm"
          locale="vi"
          clearable
          value={block.to}
          minDate={block.from ?? undefined}
          onChange={(value) => updateBlock(block.id, { to: value })}
        />
      </SimpleGrid>
      <Text fz={12} className={classes.muted} mt={-6}>
        Để trống là luôn hiển thị. Hết lịch, block tự ẩn.
      </Text>
      <TextInput
        label="Mã neo trên web"
        value={block.code}
        readOnly
        description={`Dùng cho link nhảy tới block, ví dụ hacom.vn/#${block.code}.`}
        inputWrapperOrder={["label", "input", "description"]}
        classNames={{ input: classes.mono }}
        styles={{ input: { background: "var(--hb-surface-2)" } }}
      />
    </SectionCard>
  );
};
